/**
 * Capture the visible desktop viewport on every softphone error (via
 * reportSoftphoneFailure) and attach the JPEG to a Sentry Issue event
 * (Attachments API). Also emits a searchable Sentry Log line keyed by contactId.
 *
 * Note: Sentry Logs cannot carry file attachments — the image lives on the Issue
 * event; Logs are for discovery (`module:calls contactId:"…"`).
 */
import html2canvas from 'html2canvas'
import * as Sentry from '@sentry/react'
import { callLogger } from '../Utils/ccpLogger'
import type { SoftphoneFailureNotice } from './softphoneRecovery'

const CAPTURE_DELAY_MS = 2000
const JPEG_QUALITY = 0.72

let lastCapturedFailureAt = 0
let inFlightFailureAt = 0

function waitBeforeCapture(): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, CAPTURE_DELAY_MS)
    })
}

/** Visible page root — viewport capture, not infinite scroll height. */
function resolveCaptureTarget(): HTMLElement {
    return document.body || document.documentElement
}

function dataUrlToUint8Array(dataUrl: string): Uint8Array {
    const base64 = dataUrl.split(',')[1] || ''
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
    }
    return bytes
}

async function canvasToJpegBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
    return dataUrlToUint8Array(dataUrl)
}

export interface SoftphoneScreenshotOptions {
    failure: SoftphoneFailureNotice
    contactId?: string | null
    callPhase?: string | null
}

/**
 * Screenshot the visible desktop viewport on softphone error and send it to Sentry.
 * Dedupes on `failure.at` so one classified failure produces one attachment.
 */
export async function captureSoftphoneFailureScreenshot(
    options: SoftphoneScreenshotOptions,
): Promise<string | null> {
    const { failure } = options
    if (!failure?.at || failure.at === lastCapturedFailureAt || failure.at === inFlightFailureAt) {
        return null
    }

    inFlightFailureAt = failure.at

    try {
        await waitBeforeCapture()

        const target = resolveCaptureTarget()
        if (!target.isConnected) {
            callLogger.warn('Softphone screenshot skipped — capture target not in DOM', {
                contactId: options.contactId,
                source: failure.source,
            })
            return null
        }

        // Viewport only (what the agent sees at ~1920×1080) — not full scrollHeight,
        // which produced extremely tall strips in Sentry / contact lookup.
        const viewportW = Math.max(1, Math.round(window.innerWidth))
        const viewportH = Math.max(1, Math.round(window.innerHeight))
        const scrollX = window.scrollX || window.pageXOffset || 0
        const scrollY = window.scrollY || window.pageYOffset || 0

        const canvas = await html2canvas(target, {
            logging: false,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            width: viewportW,
            height: viewportH,
            windowWidth: viewportW,
            windowHeight: viewportH,
            x: scrollX,
            y: scrollY,
            scrollX: -scrollX,
            scrollY: -scrollY,
            scale: Math.min(window.devicePixelRatio || 1, 1),
            ignoreElements: (el) => {
                const tag = el.tagName?.toLowerCase()
                return tag === 'iframe' || tag === 'video' || tag === 'audio' || tag === 'script'
            },
        })

        const bytes = await canvasToJpegBytes(canvas)
        const contactId = options.contactId || null

        const filename = `softphone-viewport-${contactId || 'unknown'}-${failure.at}.jpg`
        const message = `[softphone-ui] ${failure.title}${contactId ? ` contactId=${contactId}` : ''}`

        const eventId = Sentry.withScope((scope) => {
            if (contactId) {
                scope.setTag('contactId', contactId)
                scope.setTag('contact_id', contactId)
            }
            scope.setTag('softphone.error_type', failure.errorType)
            scope.setTag('softphone.failure_kind', failure.kind)
            scope.setTag('softphone.source', failure.source)
            scope.setTag('softphone.ui_screenshot', 'true')
            scope.setContext('softphoneFailure', {
                kind: failure.kind,
                errorType: failure.errorType,
                title: failure.title,
                summary: failure.summary,
                source: failure.source,
                at: failure.at,
                contactId,
                callPhase: options.callPhase ?? null,
                viewport: { width: viewportW, height: viewportH },
            })
            scope.addAttachment({
                filename,
                data: bytes,
                contentType: 'image/jpeg',
            })
            return Sentry.captureMessage(message, 'error')
        })

        callLogger.error('Softphone failure UI screenshot attached', {
            contactId,
            errorType: failure.errorType,
            kind: failure.kind,
            source: failure.source,
            sentryEventId: eventId,
            attachment: filename,
            viewportW,
            viewportH,
        })

        lastCapturedFailureAt = failure.at
        return eventId
    } catch (e) {
        callLogger.warn('Softphone failure screenshot failed', {
            contactId: options.contactId,
            source: failure.source,
            error: String(e),
        })
        return null
    } finally {
        if (inFlightFailureAt === failure.at) {
            inFlightFailureAt = 0
        }
    }
}

/** Test-only reset. */
export function __resetSoftphoneScreenshotForTests() {
    lastCapturedFailureAt = 0
    inFlightFailureAt = 0
}
